import React, { useState } from 'react';
import { Shield, Lock, Clock, AlertTriangle, CheckCircle, Settings, UserX, Monitor, Wifi, Database, Key } from 'lucide-react';
import Button from '../../../components/ui/Button';

const PrivacyControls = ({
  encryptionLevel,
  setEncryptionLevel,
  messageExpiration,
  setMessageExpiration,
  screenshotPrevention,
  setScreenshotPrevention,
  anonymousMode,
  setAnonymousMode,
  currentUser,
  userSubscription
}) => {
  const [activeSection, setActiveSection] = useState('encryption');
  const [showAdvanced, setShowAdvanced] = useState(false);

  const sections = [
    { id: 'encryption', label: 'Chiffrement', icon: 'Shield' },
    { id: 'privacy', label: 'Anonymat', icon: 'UserX' },
    { id: 'expiration', label: 'Expiration', icon: 'Clock' },
    { id: 'advanced', label: 'Avancé', icon: 'Settings' }
  ];

  const encryptionOptions = [
    {
      id: 'basic',
      name: 'Basique',
      description: 'Chiffrement standard TLS',
      available: true,
      features: ['Transport sécurisé', 'Protection réseau']
    },
    {
      id: 'standard',
      name: 'Standard',
      description: 'Chiffrement bout-en-bout',
      available: true,
      features: ['E2E encryption', 'Clés rotatives', 'Stockage sécurisé']
    },
    {
      id: 'high',
      name: 'Élevé',
      description: 'Chiffrement militaire AES-256',
      available: userSubscription !== 'free',
      features: ['AES-256', 'Perfect Forward Secrecy', 'Zero-knowledge']
    },
    {
      id: 'enterprise',
      name: 'Entreprise',
      description: 'Chiffrement avec HSM',
      available: userSubscription === 'business',
      features: ['HSM protection', 'Audit complet', 'Conformité RGPD']
    }
  ];

  const expirationOptions = [
    { id: 'never', label: 'Jamais', description: 'Messages permanents' },
    { id: '1hour', label: '1 heure', description: 'Suppression automatique après 1h' },
    { id: '24hours', label: '24 heures', description: 'Suppression après 1 jour' },
    { id: '7days', label: '7 jours', description: 'Suppression après 1 semaine' },
    { id: '30days', label: '30 jours', description: 'Suppression après 1 mois' }
  ];

  const renderEncryptionSection = () => (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-blue-900 mb-1">
              Protection par Chiffrement
            </h3>
            <p className="text-sm text-blue-800">
              Vos conversations sont protégées par un chiffrement bout-en-bout. 
              Seuls vous et votre interlocuteur pouvez lire les messages.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {encryptionOptions?.map((option) => (
          <div
            key={option?.id}
            className={`border rounded-lg p-4 cursor-pointer transition-colors ${
              encryptionLevel === option?.id
                ? 'border-blue-500 bg-blue-50'
                : option?.available
                ? 'border-gray-200 hover:border-gray-300' :'border-gray-100 bg-gray-50 opacity-50'
            }`}
            onClick={() => option?.available && setEncryptionLevel(option?.id)}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <h4 className={`font-medium ${option?.available ? 'text-gray-900' : 'text-gray-500'}`}>
                    {option?.name}
                  </h4>
                  {encryptionLevel === option?.id && (
                    <CheckCircle className="w-4 h-4 text-blue-500" />
                  )}
                  {!option?.available && (
                    <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded text-xs font-medium">
                      Premium
                    </span>
                  )}
                </div>
                <p className={`text-sm mb-3 ${option?.available ? 'text-gray-600' : 'text-gray-400'}`}>
                  {option?.description}
                </p>
                <div className="flex flex-wrap gap-2">
                  {option?.features?.map((feature, index) => (
                    <span
                      key={index}
                      className={`px-2 py-1 rounded text-xs ${
                        option?.available 
                          ? 'bg-gray-100 text-gray-700' :'bg-gray-50 text-gray-400'
                      }`}
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
              <Lock className={`w-5 h-5 ${option?.available ? 'text-gray-400' : 'text-gray-300'}`} />
            </div>
          </div>
        ))}
      </div>

      {/* Current Status */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div>
            <h4 className="font-semibold text-green-900">
              Statut Actuel: {encryptionOptions?.find(opt => opt?.id === encryptionLevel)?.name}
            </h4>
            <p className="text-sm text-green-800">
              Toutes vos conversations sont sécurisées avec ce niveau de chiffrement
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderPrivacySection = () => (
    <div className="space-y-6">
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <UserX className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-purple-900 mb-1">
              Protection de l'Anonymat
            </h3>
            <p className="text-sm text-purple-800">
              Contrôlez quelles informations personnelles sont visibles par les autres utilisateurs.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h4 className="font-medium text-gray-900">Mode Anonyme</h4>
              <p className="text-sm text-gray-600">
                Masquer votre nom réel et utiliser un identifiant généré
              </p>
            </div>
            <button
              onClick={() => setAnonymousMode(!anonymousMode)}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                anonymousMode ? 'bg-blue-500' : 'bg-gray-300'
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                  anonymousMode ? 'translate-x-6' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
          {anonymousMode && (
            <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
              <p className="text-sm text-yellow-800">
                <strong>Actif:</strong> Votre nom apparaîtra comme "Professionnel {currentUser?.id?.slice(-4)}"
              </p>
            </div>
          )}
        </div>

        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h4 className="font-medium text-gray-900">Protection contre les captures d'écran</h4>
              <p className="text-sm text-gray-600">
                Empêcher les captures d'écran des conversations
              </p>
            </div>
            <button
              onClick={() => setScreenshotPrevention(!screenshotPrevention)}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                screenshotPrevention ? 'bg-blue-500' : 'bg-gray-300'
              }`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                  screenshotPrevention ? 'translate-x-6' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
          {screenshotPrevention && (
            <div className="bg-blue-50 border border-blue-200 rounded p-3">
              <p className="text-sm text-blue-800">
                Les tentatives de capture d'écran seront bloquées et signalées
              </p>
            </div>
          )}
        </div>

        <div className="border border-gray-200 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-3">Informations Visibles</h4>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Email</span>
              <span className="text-sm text-red-600 font-medium">Masqué (***@***.***)</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Téléphone</span>
              <span className="text-sm text-red-600 font-medium">Masqué (+33 ** ** ** **)</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Nom d'entreprise</span>
              <span className="text-sm text-green-600 font-medium">Visible</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Spécialisations</span>
              <span className="text-sm text-green-600 font-medium">Visible</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderExpirationSection = () => (
    <div className="space-y-6">
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Clock className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-orange-900 mb-1">
              Expiration Automatique des Messages
            </h3>
            <p className="text-sm text-orange-800">
              Configurez la durée de conservation de vos messages pour une sécurité maximale.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {expirationOptions?.map((option) => (
          <div
            key={option?.id}
            className={`border rounded-lg p-4 cursor-pointer transition-colors ${
              messageExpiration === option?.id
                ? 'border-orange-500 bg-orange-50' :'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => setMessageExpiration(option?.id)}
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-2 mb-1">
                  <h4 className="font-medium text-gray-900">{option?.label}</h4>
                  {messageExpiration === option?.id && (
                    <CheckCircle className="w-4 h-4 text-orange-500" />
                  )}
                </div>
                <p className="text-sm text-gray-600">{option?.description}</p>
              </div>
              <Clock className="w-5 h-5 text-gray-400" />
            </div>
          </div>
        ))}
      </div>

      {messageExpiration !== 'never' && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-yellow-900 mb-1">
                Attention: Messages Temporaires
              </h4>
              <p className="text-sm text-yellow-800">
                Les messages seront automatiquement supprimés après la durée sélectionnée. 
                Cette action est irréversible.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderAdvancedSection = () => (
    <div className="space-y-6">
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Settings className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-gray-900 mb-1">
              Paramètres Avancés
            </h3>
            <p className="text-sm text-gray-600">
              Options de sécurité et de confidentialité pour utilisateurs expérimentés.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center space-x-3 mb-3">
            <Key className="w-5 h-5 text-blue-600" />
            <h4 className="font-medium text-gray-900">Gestion des Clés</h4>
          </div>
          <p className="text-sm text-gray-600 mb-3">
            Contrôlez la génération et la rotation des clés de chiffrement.
          </p>
          <Button variant="outline" size="sm" disabled={userSubscription === 'free'}>
            Gérer les Clés
          </Button>
        </div>

        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center space-x-3 mb-3">
            <Database className="w-5 h-5 text-green-600" />
            <h4 className="font-medium text-gray-900">Sauvegarde Chiffrée</h4>
          </div>
          <p className="text-sm text-gray-600 mb-3">
            Sauvegardez vos conversations avec chiffrement local.
          </p>
          <Button variant="outline" size="sm" disabled={userSubscription === 'free'}>
            Configurer
          </Button>
        </div>

        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center space-x-3 mb-3">
            <Monitor className="w-5 h-5 text-purple-600" />
            <h4 className="font-medium text-gray-900">Audit des Sessions</h4>
          </div>
          <p className="text-sm text-gray-600 mb-3">
            Surveillez les connexions et l'activité de votre compte.
          </p>
          <Button variant="outline" size="sm">
            Voir l'Audit
          </Button>
        </div>

        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center space-x-3 mb-3">
            <Wifi className="w-5 h-5 text-orange-600" />
            <h4 className="font-medium text-gray-900">Réseau Sécurisé</h4>
          </div>
          <p className="text-sm text-gray-600 mb-3">
            Vérifiez la sécurité de votre connexion réseau.
          </p>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span className="text-sm text-green-600 font-medium">Sécurisé</span>
          </div>
        </div>
      </div>

      {userSubscription === 'free' && (
        <div className="bg-gradient-to-br from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-6">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-purple-900 mb-2">
              Fonctionnalités Premium
            </h3>
            <p className="text-sm text-purple-700 mb-4">
              Accédez à toutes les options de sécurité avancées avec un abonnement Premium.
            </p>
            <Button variant="default" className="bg-purple-600 hover:bg-purple-700">
              Passer Premium
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  const renderSectionContent = () => {
    switch (activeSection) {
      case 'encryption':
        return renderEncryptionSection();
      case 'privacy':
        return renderPrivacySection();
      case 'expiration':
        return renderExpirationSection();
      case 'advanced':
        return renderAdvancedSection();
      default:
        return renderEncryptionSection();
    }
  };

  return (
    <div className="flex h-full">
      {/* Settings Navigation */}
      <div className="w-64 border-r border-gray-200 bg-gray-50">
        <div className="p-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Contrôles de Confidentialité
          </h2>
          <nav className="space-y-2">
            {sections?.map((section) => (
              <button
                key={section?.id}
                onClick={() => setActiveSection(section?.id)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                  activeSection === section?.id
                    ? 'bg-blue-100 text-blue-800 font-medium' :'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {section?.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Settings Content */}
      <div className="flex-1 p-6">
        {renderSectionContent()}
      </div>
    </div>
  );
};

export default PrivacyControls;